import { useState, useCallback } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ChevronLeft, Sparkles, AlertCircle } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, FadeIn } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { DocumentUploader } from '@/components/dataroom';
import { extractPitchDeck } from '@/lib/supabase/edge-functions';
import { mode } from '@/lib/config/app-mode';
import { getMockExtraction, type ExtractedStartupData } from '@/lib/pitch-deck-extraction';
import type { UploadResult, StorageFile } from '@/lib/storage';
import { track } from '@/lib/analytics';
import { useTrackScreen } from '@/lib/analytics';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAiConsentGate } from '@/lib/consent/ai-consent';

type ProcessState = 'idle' | 'extracting' | 'error';

export default function PitchDeckUploadScreen() {
  useTrackScreen('PitchDeckUpload');
  const router = useRouter();
  const colors = useSystemThemeColors();

  const [processState, setProcessState] = useState<ProcessState>('idle');
  const [uploadedFile, setUploadedFile] = useState<StorageFile | null>(null);
  const [error, setError] = useState<string | null>(null);

  // This flow extracts, scores, AND (on document indexing) triggers team
  // extraction from the uploaded deck, so one sheet covers all three
  // (R-D-GATES / D3 addendum "Change from D2 review").
  const { runWithConsent, openConsentSheet, sheet: consentSheet } = useAiConsentGate([
    'pitch_deck_analysis',
    'ai_scoring',
    'team_extraction',
  ]);

  const handleExtract = useCallback(async (file: StorageFile) => {
    setProcessState('extracting');
    setError(null);

    try {
      let extractedData: ExtractedStartupData;

      if (mode.isMock) {
        // Demo mode: simulate processing time and use mock data
        await new Promise((resolve) => setTimeout(resolve, 2000 + Math.random() * 2000));
        extractedData = getMockExtraction();
      } else {
        // Production: call edge function with document ID
        const { data, error: extractError } = await extractPitchDeck({
          documentId: file.id,
        });

        if (extractError === 'Consent required') {
          // A race (or consent withdrawn elsewhere) — the cached "consented"
          // flag said yes, the server said no. Recover by reopening the
          // sheet and retrying extraction automatically once re-granted.
          setProcessState('idle');
          openConsentSheet(() => handleExtract(file));
          return;
        }

        if (extractError || !data) {
          throw new Error(extractError || 'Failed to extract data from pitch deck');
        }

        extractedData = data.data;
      }

      // Navigate to review screen
      router.push({
        pathname: '/(onboarding)/pitch-deck-review',
        params: {
          extractedData: JSON.stringify(extractedData),
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to analyze pitch deck';
      setError(`${message}. Please try again or use the questionnaire.`);
      setProcessState('error');
    }
  }, [router, openConsentSheet]);

  const handleUploadComplete = useCallback((result: UploadResult) => {
    if (result.success && result.file) {
      const file = result.file;
      track('document_uploaded', { type: 'pitch_deck', source: 'onboarding' });
      setUploadedFile(file);
      // Automatically start extraction after upload, gated on explicit
      // AI-processing consent (extraction, scoring, and team extraction).
      runWithConsent(() => handleExtract(file));
    }
  }, [handleExtract, runWithConsent]);

  const handleUploadError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setProcessState('error');
  }, []);

  const handleRetry = useCallback(() => {
    setProcessState('idle');
    setError(null);
    setUploadedFile(null);
  }, []);

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(500)}
          className="px-5 pt-4 pb-4"
        >
          <View className="flex-row items-center justify-between">
            <Pressable
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
            <View className="flex-row gap-2">
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.border.light }}
              />
            </View>
            <View className="w-10" />
          </View>
        </Animated.View>

        {/* Title */}
        <Animated.View
          entering={FadeInDown.delay(200).duration(500)}
          className="px-5 pb-6"
        >
          <View className="flex-row items-center gap-3 mb-2">
            <Sparkles size={24} color={colors.primary[600]} />
            <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
              AI-Powered
            </Text>
          </View>
          <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
            Upload Your Pitch Deck
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
            We'll extract your company info automatically
          </Text>
        </Animated.View>

        {/* Upload Area */}
        <View className="flex-1 px-5">
          <Animated.View entering={FadeInUp.delay(300).duration(500)}>
            {processState === 'extracting' ? (
              /* Extracting State */
              <View
                style={{
                  backgroundColor: colors.surface.primary,
                  borderRadius: 20,
                  padding: 32,
                  alignItems: 'center',
                }}
              >
                <ActivityIndicator size="large" color={colors.primary[600]} />
                <Text
                  style={{ color: colors.primary[600] }}
                  className="text-base font-semibold mt-4"
                >
                  Analyzing with AI...
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-sm mt-2 text-center">
                  Extracting company information from your pitch deck
                </Text>
              </View>
            ) : (
              /* Upload Component */
              <DocumentUploader
                bucket="pitch-decks"
                onUploadComplete={handleUploadComplete}
                onUploadError={handleUploadError}
                allowImages={false}
                title="Upload Pitch Deck"
                subtitle="PDF format, up to 100MB"
              />
            )}

            {/* Error Message */}
            {error && (
              <Animated.View
                entering={FadeIn.duration(300)}
                className="flex-row items-center gap-2 mt-4 px-4 py-3 rounded-xl"
                style={{ backgroundColor: colors.red.light }}
              >
                <AlertCircle size={20} color={colors.red.main} />
                <Text style={{ color: colors.red.main }} className="text-sm flex-1">
                  {error}
                </Text>
              </Animated.View>
            )}

            {/* Retry Button */}
            {processState === 'error' && (
              <Pressable
                onPress={handleRetry}
                className="mt-4 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <Text style={{ color: colors.primary[600] }} className="font-medium">
                  Try Again
                </Text>
              </Pressable>
            )}

            {/* Info Card */}
            <View
              className="mt-6 p-4 rounded-xl"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-2">
                What we extract:
              </Text>
              <View className="gap-1">
                {[
                  'Company name & tagline',
                  'Problem & solution',
                  'Market size & competitors',
                  'Team information',
                  'Funding details',
                ].map((item, index) => (
                  <Text key={index} style={{ color: colors.text.secondary }} className="text-sm">
                    • {item}
                  </Text>
                ))}
              </View>
            </View>
          </Animated.View>
        </View>

        {/* Alternative Option */}
        <Animated.View
          entering={FadeInUp.delay(400).duration(500)}
          className="px-5 pb-8 pt-4"
        >
          <Pressable
            onPress={() => router.replace('/(onboarding)/startup-essentials')}
            className="py-3 items-center"
            disabled={processState === 'extracting'}
          >
            <Text style={{ color: colors.text.tertiary }} className="text-sm">
              No pitch deck?{' '}
              <Text style={{ color: colors.primary[600] }} className="font-medium">
                Fill out questionnaire instead
              </Text>
            </Text>
          </Pressable>
        </Animated.View>
      </SafeAreaView>

      {consentSheet}
    </View>
  );
}
